import { useCallback, useMemo, useState } from 'react';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { Box, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import ListActionBar from 'src/components/ListActionBar';
import TableContainer from 'src/components/Table/TableContainer';
import LoadingRows from 'src/components/Table/LoadingRows';
import NoDataCell from 'src/components/NoDataCell';
import { readFilterParams, useFilterParams } from 'src/hooks/filterParams';
import {
  useInfiniteEnvironmentPolicies,
  useTogglePolicyHidden,
} from 'src/hooks/environmentWdacPolicy';
import { pagesText } from 'src/constants/pagesText';
import { PORTAL_ROUTE } from 'src/constants/routes';
import { getBasePolicyModeOptions } from 'src/utils/enums/basePolicyMode';
import { WDAC_POLICY_TYPE, getWdacPolicyTypeOptions } from 'src/utils/enums/wdacPolicyType';
import {
  POLICY_VISIBILITY,
  getPolicyVisibilityOptions,
  toIsHiddenValues,
} from 'src/utils/enums/uiOnly/policyVisibility';
import {
  POLICY_TABLE_SX,
  PolicyActionsCell,
  PolicyCaptionCell,
  PolicyHeaderCell,
  PolicyManagedCell,
  PolicyNameCell,
  PolicyTypeCell,
  toBooleanParam,
} from './PolicyTableShared';
import usePolicyManagedAction from './usePolicyManagedAction';

const PAGE_LIMIT = 50;

const TABLE_MAX_HEIGHT = 'calc(100vh - 320px)';

// The policies worth attention are the ones actually running on the fleet, so the list leads with
// the widest deployed rather than alphabetically.
const DEFAULT_SORT = { column: 'Devices', direction: 'desc' };

const DEFAULT_FILTERS = {
  policyTypes: [WDAC_POLICY_TYPE.BasePolicy, WDAC_POLICY_TYPE.SupplementalPolicy],
  visibilities: [POLICY_VISIBILITY.Visible],
  basePolicyMode: '',
  isPortalManaged: '',
};

const POLICY_COLUMNS = [
  { key: 'actions', label: 'Actions', width: '120px' },
  { key: 'policyName', label: 'Policy Name', sortKey: 'PolicyName', width: '30%' },
  { key: 'policyVersion', label: 'Policy Version', sortKey: 'PolicyVersion', width: '14%' },
  { key: 'policyType', label: 'Policy Type', sortKey: 'PolicyType', width: '20%' },
  { key: 'managed', label: 'Managed', sortKey: 'IsPortalManaged', width: '16%' },
  { key: 'devices', label: 'Devices', sortKey: 'Devices', width: '100px' },
];

const isBasePolicyOnlySelection = policyTypes =>
  policyTypes?.length === 1 && policyTypes[0] === WDAC_POLICY_TYPE.BasePolicy;

// Mode is only a meaningful narrowing when supplemental policies are already excluded.
const FILTER_OPTIONS = [
  {
    key: 'policyTypes',
    type: 'checkbox',
    label: 'Type',
    options: getWdacPolicyTypeOptions(),
  },
  {
    key: 'visibilities',
    type: 'checkbox',
    label: 'Visibility',
    options: getPolicyVisibilityOptions(),
  },
  {
    key: 'basePolicyMode',
    type: 'select',
    label: 'Base Policy Mode',
    options: [{ value: '', label: 'All modes' }, ...getBasePolicyModeOptions()],
    disabled: currentFilters => !isBasePolicyOnlySelection(currentFilters.policyTypes),
  },
  {
    key: 'isPortalManaged',
    type: 'select',
    label: 'Managed',
    options: [
      { value: '', label: 'All policies' },
      { value: 'true', label: 'Managed' },
      { value: 'false', label: 'Not managed' },
    ],
  },
];

// Detected policies are the ones a device has reported, so the list is fixed to that and the rest
// of the query is whatever the filters narrow it to.
const DetectedPoliciesTable = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [searchValue, setSearchValue] = useState('');
  const [filters, setFilters] = useState(
    () => readFilterParams(DEFAULT_FILTERS) ?? DEFAULT_FILTERS,
  );
  const [sort, setSort] = useState(DEFAULT_SORT);

  const { onToggleManaged, managedDisabled, managedDialogs } = usePolicyManagedAction();
  const toggleHiddenMutation = useTogglePolicyHidden();

  useFilterParams(filters);

  const handleSort = useCallback(column => {
    if (!column.sortKey) {
      return;
    }

    setSort(prev =>
      prev.column === column.sortKey
        ? { column: column.sortKey, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { column: column.sortKey, direction: 'asc' },
    );
  }, []);

  // The api layer rejects with the backend's message as a plain string, and the business rules
  // behind these toggles are worth showing verbatim rather than as a generic failure.
  const showMutationError = useCallback(
    error =>
      enqueueSnackbar(typeof error === 'string' && error ? error : pagesText.somethingWentWrong, {
        variant: 'error',
        autoHideDuration: 4000,
      }),
    [enqueueSnackbar],
  );

  const handleToggleHidden = useCallback(
    async policy => {
      try {
        await toggleHiddenMutation.mutateAsync({ envPolicyId: policy.id });
        enqueueSnackbar(policy.isHidden ? 'Policy unhidden.' : 'Policy hidden.', {
          variant: 'success',
          autoHideDuration: 3000,
        });
      } catch (error) {
        showMutationError(error);
      }
    },
    [enqueueSnackbar, showMutationError, toggleHiddenMutation],
  );

  const isBasePolicyOnly = isBasePolicyOnlySelection(filters.policyTypes);

  const apiQuery = useMemo(() => {
    const params = { isReported: true };
    const trimmed = searchValue.trim();
    if (trimmed) {
      params.search = trimmed;
    }
    if (filters.policyTypes.length) {
      params.policyTypes = filters.policyTypes;
    }
    if (filters.visibilities.length) {
      params.isHidden = toIsHiddenValues(filters.visibilities);
    }
    if (isBasePolicyOnly && filters.basePolicyMode) {
      params.basePolicyMode = filters.basePolicyMode;
    }
    params.isPortalManaged = toBooleanParam(filters.isPortalManaged);
    params.sortColumn = sort.column;
    params.sortDesc = sort.direction === 'desc';
    return params;
  }, [searchValue, filters, isBasePolicyOnly, sort]);

  const policiesQuery = useInfiniteEnvironmentPolicies({ apiQuery, limit: PAGE_LIMIT });

  const policies = useMemo(
    () => policiesQuery.data?.pages?.flatMap(page => page.results || []) || [],
    [policiesQuery.data],
  );
  const totalCount = policiesQuery.data?.pages?.[0]?.totalCount || 0;

  const loadMore = useCallback(() => {
    if (policiesQuery.hasNextPage) {
      policiesQuery.fetchNextPage();
    }
  }, [policiesQuery]);

  const [sentryRef] = useInfiniteScroll({
    loading: policiesQuery.isFetchingNextPage,
    hasNextPage: policiesQuery.hasNextPage,
    disabled: false,
    onLoadMore: loadMore,
    rootMargin: '0px 0px 400px 0px',
  });

  const handleFilterChange = useCallback(nextFilters => {
    const policyTypes = nextFilters.policyTypes || [];
    const baseOnly = isBasePolicyOnlySelection(policyTypes);

    setFilters({
      policyTypes,
      visibilities: nextFilters.visibilities || [],
      // Dropped as soon as supplemental policies come back into scope, so the chips never claim a
      // narrowing that is not being applied.
      basePolicyMode: baseOnly ? nextFilters.basePolicyMode || '' : '',
      isPortalManaged: nextFilters.isPortalManaged || '',
    });
  }, []);

  const handleRowClick = useCallback(
    policyId => navigate(`${PORTAL_ROUTE}/policy-details/${policyId}/general`),
    [navigate],
  );

  const renderCell = useCallback(
    (column, policy) => {
      switch (column.key) {
        case 'actions':
          return (
            <PolicyActionsCell
              key={`${policy.id}-actions`}
              policy={policy}
              hiddenDisabled={toggleHiddenMutation.isLoading}
              managedDisabled={managedDisabled}
              onToggleManaged={onToggleManaged}
              onToggleHidden={handleToggleHidden}
            />
          );
        case 'policyName':
          return (
            <PolicyNameCell
              key={`${policy.id}-name`}
              policyName={policy.policyName}
              policyId={policy.policyId}
              onOpen={() => handleRowClick(policy.id)}
            />
          );
        case 'policyVersion':
          return (
            <PolicyCaptionCell key={`${policy.id}-version`}>
              {policy.policyVersion || '—'}
            </PolicyCaptionCell>
          );
        case 'policyType':
          return (
            <PolicyTypeCell
              key={`${policy.id}-type`}
              policyType={policy.policyType}
              basePolicyMode={policy.basePolicyMode}
            />
          );
        case 'devices':
          return (
            <PolicyCaptionCell key={`${policy.id}-devices`}>
              {policy.devicesCount ?? 0}
            </PolicyCaptionCell>
          );
        case 'managed':
          return <PolicyManagedCell key={`${policy.id}-managed`} policy={policy} />;
        default:
          return <TableCell key={`${policy.id}-${column.key}`} />;
      }
    },
    [
      handleRowClick,
      onToggleManaged,
      handleToggleHidden,
      toggleHiddenMutation.isLoading,
      managedDisabled,
    ],
  );

  return (
    <Box sx={{ position: 'relative' }}>
      <ListActionBar
        onSearch={setSearchValue}
        defaultSearch={searchValue}
        onResetSearch={() => setSearchValue('')}
        searchPlaceholder="Policy name or GUID"
        filters={filters}
        onFilterChange={handleFilterChange}
        filterOptions={FILTER_OPTIONS}
        filterChips
        limitChips={3}
        filterButtonCount={totalCount}
      />

      <TableContainer roundedTop styles={{ maxHeight: TABLE_MAX_HEIGHT }}>
        <Table stickyHeader size="small" sx={POLICY_TABLE_SX}>
          <colgroup>
            {POLICY_COLUMNS.map(column => (
              <col key={column.key} style={{ width: column.width }} />
            ))}
          </colgroup>
          <TableHead sx={{ backgroundColor: '#3F3F3F' }}>
            <TableRow>
              {POLICY_COLUMNS.map(column => (
                <PolicyHeaderCell
                  key={column.key}
                  column={column}
                  sort={sort}
                  onSort={handleSort}
                />
              ))}
            </TableRow>
          </TableHead>
          <TableBody sx={{ backgroundColor: '#4B4B4B' }}>
            {policiesQuery.isLoading ? (
              <LoadingRows columnCount={POLICY_COLUMNS.length} rowCount={10} />
            ) : (
              <>
                {policies.map((policy, index) => (
                  <TableRow
                    key={policy.id}
                    ref={index === policies.length - 1 ? sentryRef : null}
                    hover
                    onClick={() => handleRowClick(policy.id)}
                    sx={{ cursor: 'pointer' }}>
                    {POLICY_COLUMNS.map(column => renderCell(column, policy))}
                  </TableRow>
                ))}
                {policiesQuery.isFetchingNextPage ? (
                  <LoadingRows columnCount={POLICY_COLUMNS.length} rowCount={3} />
                ) : null}
                {!policiesQuery.isFetchingNextPage && !policies.length ? (
                  <NoDataCell colSpan={POLICY_COLUMNS.length}>
                    No policies have been detected on devices yet.
                  </NoDataCell>
                ) : null}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {managedDialogs}
    </Box>
  );
};

export default DetectedPoliciesTable;
